// INTELIMOVE-SAAS-003 (IM-ADR-021): administração dos usuários de cada organização (tenant), separada da administração
// da plataforma (platform.ts). Uma única matriz de permissões, aplicada no backend (`requireTenantPermission`) e refletida
// na interface; a regra por alvo (`memberActionBlock`) é a mesma que a função `membership_rule` aplica no banco.
import { z } from 'zod';
import type { Role } from './index.js';

export const ROLE_NAME: Record<Role, string> = { OWNER: 'Proprietário', ADMIN: 'Administrador', MEMBER: 'Membro' };

/**
 * IM-SEC-117: matriz de permissões da administração da organização. Rota nova da administração declara UMA permissão
 * desta lista. Permissões dos módulos do produto (fontes, concorrentes, integrações…) continuam nas regras de cada módulo
 * e não mudam aqui.
 */
export const TENANT_PERMISSIONS = {
  /** Dados cadastrais da organização ativa e o próprio perfil. */
  'organization.profile.read': ['OWNER', 'ADMIN', 'MEMBER'],
  /** Matriz de perfis e permissões (o que cada perfil pode fazer). */
  'organization.permissions.read': ['OWNER', 'ADMIN', 'MEMBER'],
  /** Lista de usuários, situação dos vínculos e convites. */
  'organization.members.read': ['OWNER', 'ADMIN'],
  /** Convidar, reemitir e revogar convites (perfis Administrador e Membro). */
  'organization.invitations.manage': ['OWNER', 'ADMIN'],
  /** Alterar perfil, desativar, reativar e remover vínculos — sujeito à regra por alvo. */
  'organization.members.manage': ['OWNER', 'ADMIN'],
  /** Conceder ou retirar o perfil Proprietário e transferir a propriedade. */
  'organization.ownership.manage': ['OWNER'],
  /** Histórico administrativo (ingresso, perfis, situação, convites). */
  'organization.history.read': ['OWNER', 'ADMIN'],
  /** Plano, recursos, assentos, consumo e créditos da organização (somente leitura; SAAS-005). */
  'organization.subscription.read': ['OWNER', 'ADMIN'],
  /** Trilha de auditoria técnica da organização (IM-API-016). */
  'organization.audit.read': ['OWNER', 'ADMIN'],
} as const satisfies Record<string, readonly Role[]>;
export type TenantPermission = keyof typeof TENANT_PERMISSIONS;
export const tenantCan = (role: Role | null | undefined, permission: TenantPermission): boolean =>
  !!role && (TENANT_PERMISSIONS[permission] as readonly Role[]).includes(role);

export const TENANT_PERMISSION_LABEL: Record<TenantPermission, string> = {
  'organization.profile.read': 'Ver os dados da empresa e o próprio perfil',
  'organization.permissions.read': 'Consultar perfis e permissões',
  'organization.members.read': 'Ver usuários, situação dos acessos e convites',
  'organization.invitations.manage': 'Convidar pessoas e revogar ou reemitir convites',
  'organization.members.manage': 'Alterar perfil, desativar, reativar e remover usuários',
  'organization.ownership.manage': 'Conceder ou retirar o perfil Proprietário e transferir a propriedade',
  'organization.history.read': 'Consultar o histórico administrativo',
  'organization.subscription.read': 'Consultar assinatura, consumo e créditos',
  'organization.audit.read': 'Consultar a trilha de auditoria técnica',
};

/**
 * Regras dos módulos do produto (já existentes, não alteradas nesta etapa) — mostradas na tela de perfis para o
 * administrador entender o efeito de cada perfil. A fonte de verdade é o backend de cada módulo.
 */
export const TENANT_PRODUCT_RULES: Array<{ label: string; roles: readonly Role[] }> = [
  { label: 'Usar objetivos, empresas-alvo, sinais, análises, decisões e planejamento', roles: ['OWNER', 'ADMIN', 'MEMBER'] },
  { label: 'Configurar fontes monitoradas, concorrentes, battlecards, cenários e integrações', roles: ['OWNER', 'ADMIN'] },
];

export const MEMBER_ACTIONS = ['SET_ROLE', 'DEACTIVATE', 'REACTIVATE', 'REMOVE'] as const;
export type MemberAction = (typeof MEMBER_ACTIONS)[number];
export type MembershipStatus = 'ACTIVE' | 'INACTIVE';
export type MembershipBlockScope = 'ORGANIZACAO' | 'PLATAFORMA';

/** Códigos de bloqueio da regra por alvo (mesmos de `membership_rule`, IM-MIG-038). */
export const MEMBER_BLOCK_MESSAGE = {
  invalid_action: 'Operação não permitida.',
  forbidden: 'Operação não permitida para o seu perfil.',
  self_action: 'Você não pode alterar o próprio acesso. Peça a outro administrador ou use a transferência de propriedade.',
  owner_only: 'Somente um proprietário pode alterar proprietários ou conceder esse perfil.',
  platform_block: 'Este acesso foi bloqueado pela administração da plataforma. Fale com o suporte do InteliMove.',
  member_inactive: 'Reative o acesso antes de alterar o perfil.',
  role_unchanged: 'O usuário já tem esse perfil.',
  status_unchanged: 'O acesso já está nessa situação.',
} as const;
export type MemberBlockCode = keyof typeof MEMBER_BLOCK_MESSAGE;

export interface MemberActionInput {
  scope: 'ORGANIZACAO' | 'PLATAFORMA';
  actorId: string;
  /** Papel do ator na organização (escopo ORGANIZACAO). Na plataforma, ignorado. */
  actorRole: Role | null;
  target: { id: string; role: Role; status: MembershipStatus; blockScope: MembershipBlockScope | null };
  action: MemberAction;
  newRole?: Role | null;
}

/**
 * Regra por alvo (IM-SEC-118). Devolve o código do bloqueio ou null. A proteção do último proprietário ativo é do banco
 * (gatilho `memberships_guard`) e vale para qualquer caminho.
 */
export function memberActionBlock(i: MemberActionInput): MemberBlockCode | null {
  const newRole = i.newRole ?? null;
  if (!(MEMBER_ACTIONS as readonly string[]).includes(i.action)) return 'invalid_action';
  if (i.action === 'SET_ROLE' && (!newRole || !['OWNER', 'ADMIN', 'MEMBER'].includes(newRole))) return 'invalid_action';
  if (i.scope === 'ORGANIZACAO' && (!i.actorRole || !['OWNER', 'ADMIN'].includes(i.actorRole))) return 'forbidden';
  // Ninguém age sobre o próprio vínculo — nem pela plataforma (sem autoelevação).
  if (i.actorId === i.target.id) return 'self_action';
  if (i.scope === 'ORGANIZACAO') {
    if (i.actorRole === 'ADMIN' && (i.target.role === 'OWNER' || newRole === 'OWNER')) return 'owner_only';
    if (i.action === 'REACTIVATE' && i.target.blockScope === 'PLATAFORMA') return 'platform_block';
  } else if (i.action === 'REMOVE') return 'invalid_action';
  if (i.action === 'SET_ROLE') {
    if (i.target.status !== 'ACTIVE') return 'member_inactive';
    if (newRole === i.target.role) return 'role_unchanged';
  } else if (i.action === 'DEACTIVATE' && i.target.status !== 'ACTIVE') return 'status_unchanged';
  else if (i.action === 'REACTIVATE' && i.target.status === 'ACTIVE') return 'status_unchanged';
  return null;
}

/** Perfis que o ator pode atribuir (convite ou alteração). Convite nunca cria proprietário. */
export function assignableRoles(actorRole: Role | null | undefined, purpose: 'invite' | 'change'): Role[] {
  if (actorRole === 'OWNER') return purpose === 'invite' ? ['ADMIN', 'MEMBER'] : ['OWNER', 'ADMIN', 'MEMBER'];
  if (actorRole === 'ADMIN') return ['ADMIN', 'MEMBER'];
  return [];
}

const Reason = z.string().trim().min(3, 'Explique o motivo (mínimo de 3 caracteres).').max(500);
const OptionalReason = z.string().trim().max(500).optional().transform((v) => (v ? v : undefined))
  .refine((v) => v === undefined || v.length >= 3, 'Explique o motivo (mínimo de 3 caracteres).');

/** IM-API-380: alteração de perfil (motivo opcional; obrigatório ao retirar o perfil Proprietário — conferido na API). */
export const ChangeMemberRoleRequest = z.object({ role: z.enum(['OWNER', 'ADMIN', 'MEMBER']), reason: OptionalReason }).strict();
/** IM-API-381/383: desativação e remoção — motivo e confirmação obrigatórios. */
export const DeactivateMemberRequest = z.object({ reason: Reason, confirm: z.literal(true) }).strict();
export const RemoveMemberRequest = z.object({ reason: Reason, confirm: z.literal(true) }).strict();
/** IM-API-382: reativação (motivo opcional). */
export const ReactivateMemberRequest = z.object({ reason: OptionalReason }).strict();
/** IM-API-384: transferência controlada de propriedade. */
export const TransferOwnershipRequest = z.object({
  userId: z.string().uuid(), stayAs: z.enum(['OWNER', 'ADMIN', 'MEMBER']), reason: Reason, confirm: z.literal(true),
}).strict();

export const MEMBER_SORTS = ['name', 'email', 'role', 'joined', 'lastAccess'] as const;
export const MembersQuery = z.object({
  q: z.string().trim().max(120).optional(),
  status: z.enum(['ACTIVE', 'INACTIVE']).optional(),
  role: z.enum(['OWNER', 'ADMIN', 'MEMBER']).optional(),
  sort: z.enum(MEMBER_SORTS).default('name'),
  dir: z.enum(['asc', 'desc']).default('asc'),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  offset: z.coerce.number().int().min(0).max(100_000).default(0),
}).strict();

export interface TenantMemberDto {
  userId: string;
  name: string;
  email: string;
  role: Role;
  status: MembershipStatus;
  /** Quem desativou (organização ou plataforma), quando inativo. */
  blockScope: MembershipBlockScope | null;
  statusReason: string | null;
  statusChangedAt: string | null;
  joinedAt: string;
  lastAccessAt: string | null;
  /** Quem convidou (convite aceito), quando registrado. */
  invitedBy: string | null;
  /** Última mudança de perfil: quem concedeu (ou "Administração da plataforma"). */
  roleGrantedBy: string | null;
  roleChangedAt: string | null;
  /** Conta global desativada (afeta todas as organizações da pessoa; não é gerida pela organização). */
  accountDisabled: boolean;
  isSelf: boolean;
}

export interface TenantMembersDto {
  items: TenantMemberDto[];
  total: number;
  limit: number;
  offset: number;
  counts: { active: number; inactive: number; owners: number; admins: number; members: number; pendingInvitations: number };
}

export const INVITATION_STATES = ['PENDENTE', 'ACEITO', 'REVOGADO', 'EXPIRADO'] as const;
export const InvitationsQuery = z.object({ state: z.enum(INVITATION_STATES).optional() }).strict();

export const ADMIN_EVENT_KINDS = ['INGRESSO', 'PERFIL_ALTERADO', 'DESATIVADO', 'REATIVADO', 'REMOVIDO', 'PROPRIEDADE_TRANSFERIDA',
  'CONVITE_CRIADO', 'CONVITE_REEMITIDO', 'CONVITE_REVOGADO', 'CONVITE_ACEITO'] as const;
export type AdminEventKind = (typeof ADMIN_EVENT_KINDS)[number];
export const ADMIN_EVENT_LABEL: Record<AdminEventKind, string> = {
  INGRESSO: 'Ingresso', PERFIL_ALTERADO: 'Perfil alterado', DESATIVADO: 'Acesso desativado', REATIVADO: 'Acesso reativado',
  REMOVIDO: 'Vínculo encerrado', PROPRIEDADE_TRANSFERIDA: 'Propriedade transferida', CONVITE_CRIADO: 'Convite criado',
  CONVITE_REEMITIDO: 'Convite reemitido', CONVITE_REVOGADO: 'Convite revogado', CONVITE_ACEITO: 'Convite aceito',
};
export const HistoryQuery = z.object({
  kind: z.enum(ADMIN_EVENT_KINDS).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
  offset: z.coerce.number().int().min(0).max(100_000).default(0),
}).strict();

export interface AdminEventDto {
  id: string;
  kind: AdminEventKind;
  subjectName: string | null;
  subjectEmail: string | null;
  fromRole: Role | null;
  toRole: Role | null;
  fromStatus: MembershipStatus | null;
  toStatus: MembershipStatus | null;
  reason: string | null;
  /** Nome de quem fez (membro da organização); na plataforma, só o escopo — o operador não é identificado ao cliente. */
  actorName: string | null;
  actorScope: 'ORGANIZACAO' | 'PLATAFORMA' | 'SISTEMA';
  at: string;
}

/**
 * IM-API-387: assinatura e uso de usuários. Sem plano comercial nesta etapa (SAAS-005): limites e vagas são `null`
 * (não definidos) — nunca um número inventado. Organizações existentes são LEGADO (mesmos direitos de hoje).
 */

/** IM-API-379: dados da organização ativa (somente leitura). */
export interface TenantOrganizationDto {
  id: string;
  name: string;
  slug: string;
  purpose: 'REAL' | 'TECNICO' | 'DEMONSTRACAO';
  createdAt: string;
  registration: { legalName: string | null; tradeName: string | null; taxId: string | null; primaryDomain: string | null; city: string | null; region: string | null; country: string | null };
  myRole: Role;
  myPermissions: TenantPermission[];
  ownerCount: number;
}

/** IM-API-388: prévia pública do convite (quem tem o link). */
export interface InvitationPreviewDto { organizationName: string; role: 'ADMIN' | 'MEMBER'; emailHint: string; expiresAt: string }
export const InvitationSignupRequest = z.object({
  token: z.string().min(20).max(128),
  name: z.string().trim().min(2).max(120),
  email: z.string().trim().toLowerCase().email().max(254),
  password: z.string().min(10).max(512),
}).strict();

// Plataforma (IM-API-390..392): metadados dos membros e operações assistidas.
export interface PlatformOrganizationMembersDto {
  counts: { total: number; active: number; inactive: number; owners: number; admins: number; members: number; pendingInvitations: number };
  members: Array<{ userId: string; name: string; email: string; role: Role; status: MembershipStatus; statusScope: MembershipBlockScope | null;
    accountStatus: string; since: string; lastAccessAt: string | null; statusChangedAt: string | null }>;
  invitations: Array<{ id: string; email: string; role: 'ADMIN' | 'MEMBER'; createdAt: string; expiresAt: string; invitedByName: string | null }>;
}
const TotpCode = z.string().trim().regex(/^\d{6}$/, 'Informe os 6 dígitos do aplicativo autenticador.');
const PlatformReason = z.string().trim().min(5, 'Explique o motivo (mínimo de 5 caracteres).').max(500);
export const PlatformMemberChangeRequest = z.object({
  action: z.enum(['SET_ROLE', 'DEACTIVATE', 'REACTIVATE']),
  role: z.enum(['OWNER', 'ADMIN', 'MEMBER']).optional(),
  reason: PlatformReason, confirm: z.string().trim().min(1).max(120), code: TotpCode,
}).strict().refine((b) => b.action !== 'SET_ROLE' || !!b.role, { message: 'Informe o novo perfil.', path: ['role'] });
export const PlatformRevokeInvitationRequest = z.object({ reason: PlatformReason, confirm: z.string().trim().min(1).max(120), code: TotpCode }).strict();
